import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import "./MyOnboarding.css";


const API_ROOT =
  "/api/onboarding-packs";


function authHeaders(
  includeJson = true
) {
  const headers = {};

  if (includeJson) {
    headers[
      "Content-Type"
    ] = "application/json";
  }

  const token =
    localStorage.getItem(
      "access_token"
    ) ||
    localStorage.getItem(
      "token"
    ) ||
    localStorage.getItem(
      "auth_token"
    );

  if (token) {
    headers.Authorization =
      `Bearer ${token}`;
  }

  return headers;
}


async function apiFetch(
  path,
  options = {}
) {
  const response = await fetch(
    `${API_ROOT}${path}`,
    {
      credentials: "include",
      ...options,
      headers: {
        ...authHeaders(
          !(
            options.body
            instanceof FormData
          )
        ),
        ...(
          options.headers
          || {}
        ),
      },
    }
  );

  let payload = null;

  try {
    payload =
      await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    let detail =
      payload?.detail?.message ||
      payload?.detail ||
      payload?.message ||
      `HTTP ${response.status}`;

    if (
      typeof detail
      !== "string"
    ) {
      detail =
        JSON.stringify(
          detail
        );
    }

    throw new Error(
      detail
    );
  }

  return payload;
}


function StatusBadge({
  status,
}) {
  const normalized =
    String(
      status
      || "not_started"
    )
    .toLowerCase();

  return (
    <span
      className={
        `rx-onboarding-status `
        + `rx-onboarding-status--${normalized}`
      }
    >
      {
        normalized
        .replaceAll(
          "_",
          " "
        )
        .toUpperCase()
      }
    </span>
  );
}


function ProgressBar({
  value,
}) {
  const safe = Math.max(
    0,
    Math.min(
      100,
      Number(
        value
        || 0
      )
    )
  );

  return (
    <div className="rx-onboarding-progress">
      <div
        className="rx-onboarding-progress__bar"
        style={{
          width: `${safe}%`,
        }}
      />

      <span>
        {Math.round(safe)}%
      </span>
    </div>
  );
}


function StructuredForm({
  onboardingId,
  component,
  onRefresh,
}) {
  const [form, setForm] =
    useState(
      component.responses
      || {}
    );

  const [busy, setBusy] =
    useState(false);

  const save = async () => {
    setBusy(true);

    try {
      await apiFetch(
        `/my/${onboardingId}`
        + `/components/${component.key}/form`,
        {
          method: "PUT",
          body: JSON.stringify(
            form
          ),
        }
      );

      await onRefresh();

    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rx-onboarding-form">
      {
        (
          component.form_fields
          || []
        ).map(
          (field) => (
            <label
              key={field.key}
            >
              {field.label}

              {field.required && (
                <span className="rx-onboarding-required">
                  Required
                </span>
              )}

              {
                field.field_type
                === "textarea"
                ? (
                  <textarea
                    rows={4}
                    value={
                      form[
                        field.key
                      ]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setForm(
                          (current) => ({
                            ...current,
                            [field.key]:
                              event.target.value,
                          })
                        )
                    }
                  />
                )
                : (
                  <input
                    type={
                      field.field_type
                      === "date"
                        ? "date"
                        : field.field_type
                        === "email"
                        ? "email"
                        : field.field_type
                        === "number"
                        ? "number"
                        : "text"
                    }
                    value={
                      form[
                        field.key
                      ]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setForm(
                          (current) => ({
                            ...current,
                            [field.key]:
                              event.target.value,
                          })
                        )
                    }
                  />
                )
              }

              {
                field.help_text
                && (
                  <small>
                    {
                      field.help_text
                    }
                  </small>
                )
              }
            </label>
          )
        )
      }

      <button
        type="button"
        className="rx-onboarding-button rx-onboarding-button--primary"
        onClick={save}
        disabled={busy}
      >
        {
          busy
            ? "Saving..."
            : "Save & Continue"
        }
      </button>
    </div>
  );
}


function DocumentUpload({
  onboardingId,
  component,
  onRefresh,
}) {
  const [busyKey, setBusyKey] =
    useState("");

  const uploaded = new Map(
    (
      component.uploaded_documents
      || []
    ).map(
      (item) => [
        item.requirement_key,
        item,
      ]
    )
  );

  const upload = async (
    requirement,
    file
  ) => {
    if (!file) {
      return;
    }

    setBusyKey(
      requirement.key
    );

    try {
      const form =
        new FormData();

      form.append(
        "requirement_key",
        requirement.key
      );

      form.append(
        "file",
        file
      );

      await apiFetch(
        `/my/${onboardingId}`
        + `/components/${component.key}/documents`,
        {
          method: "POST",
          body: form,
        }
      );

      await onRefresh();

    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="rx-document-list">
      {
        (
          component.document_requirements
          || []
        ).map(
          (requirement) => {
            const existing =
              uploaded.get(
                requirement.key
              );

            return (
              <div
                className="rx-document-requirement"
                key={
                  requirement.key
                }
              >
                <div>
                  <strong>
                    {
                      requirement.label
                    }
                  </strong>

                  {
                    requirement.required
                    && (
                      <span className="rx-onboarding-required">
                        Required
                      </span>
                    )
                  }

                  {
                    requirement.description
                    && (
                      <small>
                        {
                          requirement.description
                        }
                      </small>
                    )
                  }

                  {
                    existing
                    && (
                      <div className="rx-uploaded-file">
                        ✓ {
                          existing.original_filename
                        }
                      </div>
                    )
                  }
                </div>

                <label className="rx-upload-button">
                  {
                    busyKey
                    === requirement.key
                      ? "Uploading..."
                      : existing
                      ? "Replace"
                      : "Upload"
                  }

                  <input
                    type="file"
                    disabled={
                      busyKey
                      === requirement.key
                    }
                    accept={
                      (
                        requirement.accepted_types
                        || []
                      ).join(",")
                    }
                    onChange={
                      (event) => {
                        console.log(
                          "RX_UPLOAD_SELECTED",
                          requirement.key,
                          event.target.files?.[0]
                        );

                        upload(
                          requirement,
                          event.target.files?.[0]
                        );
                      }
                    }
                  />
                </label>
              </div>
            );
          }
        )
      }
    </div>
  );
}


function ComponentCard({
  onboardingId,
  component,
  onRefresh,
}) {
  const [busy, setBusy] =
    useState(false);

  const acknowledge =
    async () => {
      setBusy(true);

      try {
        await apiFetch(
          `/my/${onboardingId}`
          + `/components/${component.key}/acknowledge`,
          {
            method: "POST",
          }
        );

        await onRefresh();

      } finally {
        setBusy(false);
      }
    };

  return (
    <section className="rx-component-card">
      <div className="rx-component-card__heading">
        <div className="rx-component-number">
          {component.sequence}
        </div>

        <div>
          <h3>
            {component.label}
          </h3>

          {
            component.description
            && (
              <p>
                {
                  component.description
                }
              </p>
            )
          }
        </div>

        <StatusBadge
          status={
            component.status
          }
        />
      </div>

      {
        component.instructions
        && (
          <div className="rx-component-instructions">
            {
              component.instructions
            }
          </div>
        )
      }

      {
        component.component_type
        === "information"
        && (
          <div>
            <div className="rx-information-body">
              {
                String(
                  component.information_body
                  || ""
                )
                .split("\n")
                .map(
                  (
                    line,
                    index
                  ) => (
                    <p key={index}>
                      {
                        line
                        || "\u00A0"
                      }
                    </p>
                  )
                )
              }
            </div>

            {
              component.status
              !== "complete"
              && (
                <button
                  type="button"
                  className="rx-onboarding-button rx-onboarding-button--primary"
                  disabled={busy}
                  onClick={
                    acknowledge
                  }
                >
                  {
                    busy
                    ? "Saving..."
                    : "I have read this"
                  }
                </button>
              )
            }
          </div>
        )
      }

      {
        component.component_type
        === "structured_form"
        && (
          <StructuredForm
            onboardingId={
              onboardingId
            }
            component={
              component
            }
            onRefresh={
              onRefresh
            }
          />
        )
      }

      {
        component.component_type
        === "document_upload"
        && (
          <DocumentUpload
            onboardingId={
              onboardingId
            }
            component={
              component
            }
            onRefresh={
              onRefresh
            }
          />
        )
      }

      {
        component.component_type
        === "agreement"
        && (
          <div className="rx-locked-component">
            <strong>
              Agreement
            </strong>

            <p>
              {
                component.agreement_template_key
              }
            </p>

            <span>
              Agreement review and signing
              will be enabled in a later stage.
            </span>
          </div>
        )
      }

      {
        component.component_type
        === "secure_payroll"
        && (
          <div className="rx-locked-component">
            <strong>
              Secure Payroll
            </strong>

            <p>
              TFN, bank and payroll details
              will be collected through the
              restricted payroll vault.
            </p>

            <span>
              Not yet enabled.
            </span>
          </div>
        )
      }

      {
        component.component_type
        === "declaration"
        && (
          <div className="rx-locked-component">
            <strong>
              Final Declaration
            </strong>

            <p>
              {
                component.declaration_text
              }
            </p>

            <span>
              Declaration signing is not
              yet enabled.
            </span>
          </div>
        )
      }
    </section>
  );
}


export default function MyOnboarding() {
  const [sessions, setSessions] =
    useState([]);

  const [selected, setSelected] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");


  const loadSessions =
    useCallback(
      async () => {
        setLoading(true);
        setError("");

        try {
          const payload =
            await apiFetch(
              "/my"
            );

          setSessions(
            payload.onboarding
            || []
          );

        } catch (err) {
          setError(
            err.message
          );

        } finally {
          setLoading(false);
        }
      },
      []
    );


  const loadSession =
    useCallback(
      async (
        onboardingId
      ) => {
        setError("");

        try {
          const payload =
            await apiFetch(
              `/my/${onboardingId}`
            );

          setSelected(
            payload
          );

        } catch (err) {
          setError(
            err.message
          );
        }
      },
      []
    );


  useEffect(() => {
    loadSessions();
  }, [loadSessions]);


  const ordered =
    useMemo(
      () => (
        selected?.components
        || []
      ).slice().sort(
        (
          left,
          right
        ) =>
          Number(
            left.sequence
          )
          -
          Number(
            right.sequence
          )
      ),
      [selected]
    );


  if (selected) {
    const onboarding =
      selected.onboarding;

    return (
      <main className="rx-my-onboarding">
        <button
          type="button"
          className="rx-back-button"
          onClick={() => {
            setSelected(null);
            loadSessions();
          }}
        >
          ← My Onboarding
        </button>

        <div className="rx-onboarding-heading">
          <div>
            <div className="rx-onboarding-eyebrow">
              RAPID X ONBOARDING
            </div>

            <h1>
              {
                selected.pack.name
              }
            </h1>

            <p>
              {
                selected.pack.description
                || "Complete each required step below."
              }
            </p>
          </div>

          <div className="rx-onboarding-progress-card">
            <span>
              Overall progress
            </span>

            <strong>
              {
                Math.round(
                  onboarding
                    .completion_percent
                  || 0
                )
              }%
            </strong>

            <ProgressBar
              value={
                onboarding
                  .completion_percent
              }
            />
          </div>
        </div>

        {
          error
          && (
            <div className="rx-onboarding-error">
              {error}
            </div>
          )
        }

        <div className="rx-component-stack">
          {
            ordered.map(
              (component) => (
                <ComponentCard
                  key={
                    component.key
                  }
                  onboardingId={
                    onboarding.id
                  }
                  component={
                    component
                  }
                  onRefresh={
                    () =>
                      loadSession(
                        onboarding.id
                      )
                  }
                />
              )
            )
          }
        </div>
      </main>
    );
  }


  return (
    <main className="rx-my-onboarding">
      <div className="rx-onboarding-heading">
        <div>
          <div className="rx-onboarding-eyebrow">
            RAPID X
          </div>

          <h1>
            My Onboarding
          </h1>

          <p>
            Complete your assigned
            employment and contractor
            onboarding requirements.
          </p>
        </div>
      </div>

      {
        error
        && (
          <div className="rx-onboarding-error">
            {error}
          </div>
        )
      }

      {
        loading
        ? (
          <div className="rx-onboarding-empty">
            Loading onboarding...
          </div>
        )
        : sessions.length === 0
        ? (
          <div className="rx-onboarding-empty">
            <h3>
              No onboarding assigned
            </h3>

            <p>
              You do not currently have
              an onboarding pack to complete.
            </p>
          </div>
        )
        : (
          <div className="rx-onboarding-session-grid">
            {
              sessions.map(
                (session) => (
                  <button
                    type="button"
                    className="rx-onboarding-session-card"
                    key={
                      session.id
                      || session._id
                    }
                    onClick={
                      () =>
                        loadSession(
                          session.id
                          || session._id
                        )
                    }
                  >
                    <div>
                      <strong>
                        {
                          session
                            .pack_name_snapshot
                        }
                      </strong>

                      <StatusBadge
                        status={
                          session.status
                        }
                      />
                    </div>

                    <ProgressBar
                      value={
                        session
                          .completion_percent
                      }
                    />

                    <small>
                      {
                        session
                          .completed_required_count
                        || 0
                      }
                      {" / "}
                      {
                        session
                          .required_component_count
                        || 0
                      }
                      {" required steps complete"}
                    </small>
                  </button>
                )
              )
            }
          </div>
        )
      }
    </main>
  );
}
